import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { hrmsGetRequest, hrmsPostRequest, hrmsUpdateRequest, hrmsDeleteRequest } from "../services/index";

const API_URL = "/api/payroll";

// Fetch Payroll Records
export const fetchPayrollRecords = createAsyncThunk("payroll/fetch", async () => {
  const response = await hrmsGetRequest(API_URL);
  return response.data;
});

// Generate Payroll for Month
export const generatePayroll = createAsyncThunk("payroll/generate", async ({ month, year }) => {
  const response = await hrmsPostRequest(`${API_URL}/generate-and-save-bulk`, { month, year });
  return response.data.payrolls || response.data;
});

// Approve Payroll
export const approvePayroll = createAsyncThunk("payroll/approve", async ({ id, approvalType, status }) => {
  const endpoint = approvalType === 'hr' ? 'approve-hr' : 'approve-finance';
  const response = await hrmsUpdateRequest(`${API_URL}/${endpoint}/${id}`, { remarks: status });
  return response.data;
});

// Mark Payroll as Paid
export const markPayrollAsPaid = createAsyncThunk("payroll/markPaid", async ({ id, paymentStatus }) => {
  const response = await hrmsUpdateRequest(`${API_URL}/mark-paid/${id}`, { paymentDate: new Date(), remarks: paymentStatus });
  return response.data;
});

// Fetch Payroll by Teacher Email
export const fetchPayrollByTeacherEmail = createAsyncThunk("payroll/fetchByTeacherEmail", async ({ teacherEmail, month, year }) => {
  const response = await hrmsGetRequest(`${API_URL}/employee/${teacherEmail}/${month}/${year}`);
  return response.data;
});

// Fetch Payroll Statistics
export const fetchPayrollStats = createAsyncThunk("payroll/fetchStats", async ({ month, year }) => {
  const response = await hrmsGetRequest(`${API_URL}/summary/${month}/${year}`);
  return response.data;
});

const payrollSlice = createSlice({
  name: "payroll",
  initialState: {
    payrollRecords: [],
    payrollStats: null,
    status: "idle",
    error: null,
    isGenerating: false,
  },
  reducers: {
    clearPayroll: (state) => {
      state.payrollRecords = [];
      state.payrollStats = null;
      state.status = "idle";
      state.error = null;
    },
    setGenerating: (state, action) => {
      state.isGenerating = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Payroll Records
      .addCase(fetchPayrollRecords.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPayrollRecords.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.payrollRecords = action.payload;
      })
      .addCase(fetchPayrollRecords.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      })
      
      // Generate Payroll
      .addCase(generatePayroll.pending, (state) => {
        state.isGenerating = true;
        state.status = "loading";
      })
      .addCase(generatePayroll.fulfilled, (state, action) => {
        state.isGenerating = false;
        state.status = "succeeded";
        state.payrollRecords = action.payload;
      })
      .addCase(generatePayroll.rejected, (state, action) => {
        state.isGenerating = false;
        state.status = "failed";
        state.error = action.error.message;
      })
      
      // Approve Payroll
      .addCase(approvePayroll.fulfilled, (state, action) => {
        const index = state.payrollRecords.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.payrollRecords[index] = action.payload;
        }
      })
      
      // Mark as Paid
      .addCase(markPayrollAsPaid.fulfilled, (state, action) => {
        const index = state.payrollRecords.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.payrollRecords[index] = action.payload;
        }
      })
      
      // Fetch by Teacher Email
      .addCase(fetchPayrollByTeacherEmail.fulfilled, (state, action) => {
        state.payrollRecords = action.payload;
      })
      
      // Fetch Stats
      .addCase(fetchPayrollStats.fulfilled, (state, action) => {
        state.payrollStats = action.payload;
      });
  },
});

export const { clearPayroll, setGenerating } = payrollSlice.actions;
export default payrollSlice.reducer; 